import React from 'react';
import { Box, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';

const steps = ['FOCUS', 'FLAW', 'SCORING'];

type SurveyBreadcrumbsProps = {
  currentStepIndex: number;
  onClose: () => void;
  onBack: () => void;
  showBack: boolean;
};

const SurveyBreadcrumbs: React.FC<SurveyBreadcrumbsProps> = ({
  currentStepIndex,
  onClose,
  onBack,
  showBack,
}) => {

  // progress bar calculations
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  const stepProgressDesktop = [35, 50, 65];
  const stepProgressMobile = [17.5, 50, 81.25];

  const stepProgress = isMobile ? stepProgressMobile : stepProgressDesktop;
  const progressPercent = stepProgress[currentStepIndex];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        px: 2,
        py: 1,
      }}
    >
      {/* Back Button */}
      <Box
        onClick={onBack}
        sx={{
          visibility: showBack ? 'visible' : 'hidden',
          cursor: 'pointer',
          border: '2px solid black',
          borderRadius: '50%',
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>{'<'}</Typography>
      </Box>

      {/* Breadcrumbs */}
      <Box
        sx={{
          flexGrow: 1,
          mx: 2,
          position: 'relative',
          height: 60,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: {
            xs: 'space-evenly',
            sm: 'center',
          },
          minWidth: 0,
        }}
      >
        {/* Base gray line */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: '#ccc',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        />

        {/* Blue progress line */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: `${progressPercent}%`,
            height: 2,
            backgroundColor: '#355ADD',
            transform: 'translateY(-50%)',
            transition: 'width 0.3s',
            zIndex: 2,
          }}
        />

        {steps.map((label, index) => {
          const completed = index < currentStepIndex;
          const active = index === currentStepIndex;
          const upcoming = index > currentStepIndex;

          return (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 3,
                mx: { xs: '4px', sm: '20px' },
              }}
            >
              <Box
                sx={{
                  width: 17,
                  height: 17,
                  borderRadius: '50%',
                  backgroundColor: completed ? '#355ADD' : active ? '#355ADD' : '#BFBFBF',
                  border: `2px solid ${completed || active ? '#355ADD' : '#BFBFBF'}`,
                  color: completed ? '#fff' : upcoming ? '#fff' : '#355ADD',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                  position: 'relative',
                  top: '21px',
                }}
              >
                {completed ? (
                  <Check sx={{ fontSize: 12 }} />
                ) : active ? (
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                    }}
                  />
                ) : (
                  index + 1
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  mt: '21px',
                  fontWeight: 'bold',
                  color: active || completed ? '#355ADD' : '#BFBFBF',
                  textAlign: 'center',
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Close Button */}
      <Box
        onClick={onClose}
        sx={{
          cursor: 'pointer',
          border: '2px solid black',
          borderRadius: '50%',
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>×</Typography>
      </Box>
    </Box>
  );
};

export default SurveyBreadcrumbs;
